/**
 * Data-status panel (CSAGE-543).
 *
 * Two layers in one surface:
 *  - Customer (everyone): per-data-area freshness, scope-filtered server-side
 *    to the viewer's position in the hierarchy. "Is my data current?"
 *  - Canopy admin (internal only): the full cube/pipeline health table.
 *
 * The customer numbers come from GET /cubes/data-status, which runs a scoped
 * max-date per area through the cube compiler, so hierarchy scoping is applied
 * automatically. The admin table comes from GET /cubes/health (internal-admin
 * gated server-side); the hook is only enabled for internal admins so nobody
 * else 403-polls.
 */

import { useIsInternalAdmin } from '../api/me.js';
import {
  useDataStatus,
  useCubeHealth,
  type DataAreaFreshness,
  type DataAreaStatus,
} from '../api/cubes.js';
import { DIAGNOSIS_META, LIFECYCLE_META, cubeNeedsAttention } from '../lib/cube-health.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse a ClickHouse Date / DateTime string as UTC. */
function parse(value: string | null): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/u.exec(value);
  if (!m) return null;
  const t = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
  );
  return Number.isNaN(t) ? null : new Date(t);
}

/** Canopy datetime standard: `MMM D, YYYY`, with `, HH:mm` when a time is present. */
function formatDataDate(value: string | null): string {
  const dt = parse(value);
  if (!dt) return '–';
  const base = `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}, ${dt.getUTCFullYear()}`;
  const hasTime = /[ T]\d{2}:\d{2}/u.test(value ?? '');
  if (!hasTime) return base;
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mi = String(dt.getUTCMinutes()).padStart(2, '0');
  return `${base}, ${hh}:${mi}`;
}

function relativeDays(value: string | null): string | null {
  const dt = parse(value);
  if (!dt) return null;
  const days = Math.floor((Date.now() - dt.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

const STATUS_META: Record<DataAreaFreshness, { cls: string; label: string }> = {
  fresh: { cls: 'badge--success', label: 'Up to date' },
  stale: { cls: 'badge--danger', label: 'Stale' },
  snapshot: { cls: 'badge--info', label: 'Snapshot' },
  no_data: { cls: 'badge--neutral', label: 'No data' },
  unavailable: { cls: 'badge--warning', label: 'Unavailable' },
};

function StatusBadge({ status }: { status: DataAreaFreshness }): JSX.Element {
  const meta = STATUS_META[status];
  return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
}

function areaDetail(a: DataAreaStatus): string {
  switch (a.status) {
    case 'snapshot':
      return 'Current snapshot, refreshed daily';
    case 'no_data':
      return 'No data for your organization yet';
    case 'unavailable':
      return 'Status temporarily unavailable';
    default: {
      const rel = relativeDays(a.latest);
      return `Latest data: ${formatDataDate(a.latest)}${rel ? ` (${rel})` : ''}`;
    }
  }
}

export function DataStatusPanel(): JSX.Element {
  const isInternalAdmin = useIsInternalAdmin();
  const { data, isLoading, error, refetch } = useDataStatus();

  return (
    <div className="data-status">
      <p className="data-status-intro">
        How current your Insights data is, for your part of the organization.
      </p>

      {isLoading && <p className="data-status-muted">Checking data freshness…</p>}
      {error && (
        <div className="data-status-error" role="alert">
          <span>Could not load data status.</span>
          <button type="button" className="btn btn--sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}

      {data && data.length === 0 && !isLoading && (
        <p className="data-status-muted">No data areas are available for your scope.</p>
      )}

      {data && data.length > 0 && (
        <div className="data-status-grid">
          {data.map((a) => (
            <div className="data-status-card" key={a.area}>
              <div className="data-status-card-head">
                <span className="data-status-label">{a.label}</span>
                <StatusBadge status={a.status} />
              </div>
              <div className="data-status-meta">{areaDetail(a)}</div>
            </div>
          ))}
        </div>
      )}

      {isInternalAdmin && <AdminCubeHealth />}
    </div>
  );
}

// ─── Internal-Canopy-admin: full cube/pipeline health ──────────────────────
//
// Diagnosis/lifecycle styling and the "needs attention" rule are shared with
// the warehouse ops console (admin/CubesAdminPage) via lib/cube-health.

function AdminCubeHealth(): JSX.Element {
  const { data, isLoading, error } = useCubeHealth(true);

  // Problems float up: build-failing, then stalled feeds, then the rest.
  const rows = [...(data ?? [])].sort((a, b) => {
    const d = DIAGNOSIS_META[a.diagnosis].rank - DIAGNOSIS_META[b.diagnosis].rank;
    return d !== 0 ? d : a.name.localeCompare(b.name);
  });

  const attention = (data ?? []).filter(cubeNeedsAttention).length;

  return (
    <section className="data-status-admin">
      <div className="data-status-admin-head">
        <h3>Cube &amp; pipeline health</h3>
        <span className="data-status-muted">Canopy admin · all cubes, platform-wide</span>
        {attention > 0 && <span className="badge badge--danger">{attention} need attention</span>}
      </div>

      {isLoading && <p className="data-status-muted">Loading cube health…</p>}
      {error && (
        <p className="data-status-error" role="alert">
          Could not load cube health.
        </p>
      )}

      {rows.length > 0 && (
        <div className="data-status-table-wrap">
          <table className="data-status-table">
            <thead>
              <tr>
                <th>Cube</th>
                <th>Tier</th>
                <th>Diagnosis</th>
                <th>Data as of</th>
                <th>Last build</th>
                <th>Upstream feed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const dm = DIAGNOSIS_META[c.diagnosis];
                return (
                  <tr
                    key={c.name}
                    className={
                      c.diagnosis === 'build_failing' || c.diagnosis === 'source_stalled'
                        ? 'data-status-row--stale'
                        : undefined
                    }
                  >
                    <td>
                      <span className="data-status-cube-name">{c.name}</span>
                    </td>
                    <td>{c.tier}</td>
                    <td>
                      <span className={`badge ${dm.cls}`} title={c.diagnosisReason}>
                        {dm.label}
                      </span>
                    </td>
                    <td>{c.newestDataAt ? formatDataDate(c.newestDataAt) : '–'}</td>
                    <td>{c.lastSucceededAt ? formatDataDate(c.lastSucceededAt) : '–'}</td>
                    <td>
                      {c.feed ? (
                        <div className="data-status-feed">
                          <span className={`badge ${LIFECYCLE_META[c.feed.lifecycle].cls}`}>
                            {LIFECYCLE_META[c.feed.lifecycle].label}
                          </span>
                          <span className="data-status-feed-note" title={c.feed.note}>
                            {c.feed.note}
                          </span>
                          {c.feed.ticket && (
                            <span className="data-status-feed-ticket">{c.feed.ticket}</span>
                          )}
                        </div>
                      ) : (
                        <span className="data-status-muted">–</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
